<div class="setup">
  <header class="setup-header">
    <div class="setup-title-row">
      <h1>Setup</h1>
      <div class="setup-domain-controls">
        <label class="domain-picker" *ngIf="pickableDomains.length || domains.length">
          <span>Domain</span>
          <select class="input" [ngModel]="domain" (ngModelChange)="onDomainChange($event)">
            <option [ngValue]="''">— New setup —</option>
            <option *ngFor="let d of pickableDomains" [value]="d">{{ d }}</option>
          </select>
        </label>
        <button
          type="button"
          class="btn btn--link btn--sm"
          *ngIf="hiddenSetupDomains.length"
          (click)="toggleHiddenDomains()"
          [title]="showHiddenDomains ? 'Hide competitors and support subdomains' : 'Show competitors and support subdomains'">
          {{ showHiddenDomains ? 'Hide' : 'Show' }} {{ hiddenSetupDomains.length }} hidden
        </button>
        <button
          type="button"
          class="btn"
          *ngIf="!domain || isPrepScopingPhase"
          (click)="toggleAddDomain()">
          {{ showAddDomain ? 'Cancel' : '+ Add domain' }}
        </button>
      </div>
    </div>
    <p class="setup-subtitle">
      Take a domain from "just crawled" to "report-ready". Add a new domain below, or pick an existing one to resume.
    </p>
    <p class="setup-subtitle hidden-domains-note" *ngIf="showHiddenDomains && hiddenSetupDomains.length">
      Also listed (normally hidden — these don't get their own Finish setup):
      <span *ngFor="let h of hiddenSetupDomains; let last = last">
        <strong>{{ h.domain }}</strong> ({{ hiddenDomainReasonLabel(h.reason) }}){{ last ? '' : ' · ' }}
      </span>
    </p>
  </header>

  <!-- Add domain / competitor -->
  <section class="add-domain card card--pad" id="setup-add-panel" *ngIf="showAddDomain">
    <h2>{{ addMode === 'competitor' ? 'Add a competitor' : 'Add a domain' }}</h2>
    <p class="muted" *ngIf="addMode === 'competitor'">
      Crawl a new competitor of <strong>{{ domain }}</strong>. Scope which leaf sitemaps and how many URLs to crawl, then start.
      It will be marked as a competitor automatically, and <strong>topic prep</strong> is queued for you once every domain in the
      prep list has finished crawling — including this one.
    </p>
    <p class="muted" *ngIf="addMode !== 'competitor'">Check the sitemap, scope which leaf sitemaps and how many URLs to crawl, then start. Topic prep and <strong>Finish setup</strong> then run on their own once every domain in the prep set has landed.</p>
    <div class="add-domain-row">
      <input
        class="input"
        type="text"
        placeholder="example.com"
        [(ngModel)]="newDomainInput"
        (keyup.enter)="estimateNewDomain()" />
      <button type="button" class="btn" (click)="estimateNewDomain()" [disabled]="estimateLoading">
        {{ estimateLoading ? 'Checking…' : 'Check sitemap' }}
      </button>
      <button
        type="button"
        class="btn btn--accent"
        (click)="startNewDomainCrawl()"
        [disabled]="!!startCrawlBlockedReason"
        [title]="startCrawlBlockedReason || 'Start the crawl with the scope above'">
        {{ startingCrawl ? 'Starting…' : 'Start crawl' }}
      </button>
    </div>
    <p class="step-note" *ngIf="startCrawlBlockedReason && !startingCrawl">{{ startCrawlBlockedReason }}</p>
    <p class="error" *ngIf="estimateError">{{ estimateError }}</p>

    <div class="bot-protected-panel" *ngIf="botProtected">
      <p class="bot-protected-title">This site appears to block automated sitemap requests (bot protection).</p>
      <p class="muted">
        Open the site in your browser, download the sitemap XML (often <code>/sitemap.xml</code> or linked from
        <code>/robots.txt</code>), then upload the file(s) here. You can select multiple <strong>.xml</strong> files at once.
        If you have a sitemap index, upload every referenced leaf file too.
      </p>
      <div class="bot-protected-actions">
        <label class="btn upload-btn">
          {{ estimateLoading ? 'Parsing…' : 'Upload sitemap file(s)' }}
          <input
            type="file"
            accept=".xml,.gz,application/xml,text/xml"
            multiple
            (change)="onSitemapFilesSelected($event)"
            [disabled]="estimateLoading || !newDomainInput" />
        </label>
      </div>
    </div>

    <div class="uploaded-sitemaps-note" *ngIf="usingUploadedSitemaps && uploadedSitemapFiles.length > 0">
      <span>
        Using {{ uploadedSitemapFiles.length }} uploaded file(s):
        {{ uploadedSitemapFileNames }}
      </span>
      <button type="button" class="btn btn--link" (click)="clearUploadedSitemaps()">Clear uploads</button>
    </div>

    <div class="estimate-summary" *ngIf="estimate as est">
      <p *ngIf="est.aliasApplied && est.canonicalDomain" class="estimate-line alias-note">
        Canonical domain: <strong>{{ est.canonicalDomain }}</strong>
        <span *ngIf="est.requestedDomain"> (from {{ est.requestedDomain }})</span>
      </p>
      <p *ngIf="est.sitemapSource === 'upload'" class="estimate-note">
        Sitemap loaded from your uploaded file(s) — remote fetch was skipped.
      </p>
      <div class="estimate-grid">
        <span><span class="estimate-label">Discovered URLs:</span> {{ est.totalPagesInSitemap | number }}<span *ngIf="est.sitemapParseTruncated" class="estimate-warn"> (parse truncated)</span></span>
        <span><span class="estimate-label">Selected URLs:</span> {{ getSelectedDiscoveredUrlCount() | number }}</span>
        <span><span class="estimate-label">Included at cap ({{ est.limit }}):</span> {{ getSelectedIncludedAtCap() | number }}</span>
        <span *ngIf="neverTrackExcludedCount > 0">
          <span class="estimate-label">Low-value URLs excluded:</span> {{ neverTrackExcludedCount | number }}
        </span>
        <span><span class="estimate-label">Est. crawl cost:</span> ${{ getSelectedEstimatedCost() | number: '1.2-2' }}</span>
      </div>
      <label class="checkline dev-bypass-cap" *ngIf="isDevView()">
        <input type="checkbox" [(ngModel)]="bypassCrawlCap" (ngModelChange)="onBypassCapChange()" />
        Bypass {{ defaultCrawlLimit }}-page cap (dev — uses up to 10,000)
      </label>
      <label class="checkline dev-bypass-cap" *ngIf="isDevView()">
        <input type="checkbox" [(ngModel)]="usePerUrlFallbackCrawl" />
        Use per-URL fallback crawl (slow — exact coverage via individual tasks)
      </label>
      <div class="dev-url-preview" *ngIf="isDevView() && estimate">
        <button type="button" class="btn btn--sm" (click)="openUrlListPreview()" [disabled]="previewUrlsLoading || estimateLoading">
          {{ previewUrlsLoading ? 'Loading…' : 'Open URL list' }}
        </button>
        <button type="button" class="btn btn--sm" (click)="downloadUrlList()" [disabled]="previewUrlsLoading || estimateLoading">
          Download URLs
        </button>
      </div>

      <div *ngIf="(est.duplicateUrlsInSitemaps || 0) > 0" class="estimate-note">
        Duplicate URLs exist across sitemaps ({{ est.duplicateUrlsInSitemaps | number }}). Each unique URL is crawled once.
      </div>
      <div *ngIf="(est.sitemapParseFailures || []).length > 0" class="estimate-note warn">
        Parsed with warnings: {{ est.sitemapParseFailures?.length }} sitemap(s) failed to parse.
      </div>

      <div *ngIf="getLeafNodes(est.sitemapTree).length > 0" class="sitemap-controls">
        <div class="sitemap-controls-row">
          <label class="checkline">
            <input type="checkbox" [checked]="areAllSitemapsSelected()" (change)="setAllSitemapsSelected($any($event.target).checked)" />
            Select all leaf sitemaps
          </label>
        </div>
        <div class="sitemap-filter-row">
          <label class="strategy-label">
            Lastmod on/after:
            <input type="date" class="input" [ngModel]="minLastmodFrom" (ngModelChange)="onMinLastmodFromChange($event)" />
          </label>
          <label class="strategy-label">
            Per-sitemap cap mode:
            <select class="input" [ngModel]="perSitemapLimitMode" (ngModelChange)="onPerSitemapLimitModeChange($event)">
              <option value="recent">Most recent first</option>
              <option value="oldest">Oldest first</option>
              <option value="balanced">Balanced (newest + oldest)</option>
            </select>
          </label>
        </div>
        <div class="sitemap-filter-note">Per-sitemap cap applies after lastmod filtering using the selected mode.</div>

        <div class="sitemap-list">
          <div
            class="sitemap-item"
            *ngFor="let leaf of getLeafNodes(est.sitemapTree); trackBy: trackSitemap"
            [class.sitemap-item-disabled]="!selectedSitemaps.has(leaf.sitemapUrl)">
            <div class="sitemap-item-header">
              <label class="checkline">
                <input
                  type="checkbox"
                  [checked]="selectedSitemaps.has(leaf.sitemapUrl)"
                  (change)="toggleSitemapSelection(leaf.sitemapUrl, $any($event.target).checked)" />
                <span class="sitemap-url">{{ displaySitemapLabel(leaf.sitemapUrl) }}</span>
                <span class="sitemap-meta">({{ getLeafDisplayUrlCount(leaf) | number }} URLs)</span>
              </label>
              <label class="leaf-limit-label">
                Crawl
                <input
                  type="number"
                  min="1"
                  step="1"
                  class="input leaf-limit-input"
                  [ngModel]="getPerSitemapLimit(leaf.sitemapUrl)"
                  (ngModelChange)="onPerSitemapLimitChange(leaf.sitemapUrl, $event)"
                  placeholder="all" />
                URLs
              </label>
            </div>
            <div class="sitemap-examples" *ngIf="(leaf.exampleUrls || []).length > 0">
              <span class="sitemap-examples-title">Examples:</span>
              <a
                *ngFor="let exampleUrl of (leaf.exampleUrls || []).slice(0, 2)"
                class="sitemap-example-link"
                [href]="exampleUrl"
                target="_blank"
                rel="noopener noreferrer">
                {{ exampleUrl }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- FW-ONB-01 (SS001-19): never-track suggestions, before a single page is paid for -->
      <div *ngIf="neverTrackGroups.length > 0" class="sitemap-controls never-track-controls">
        <div class="never-track-warning">
          <strong>We found low-SEO-value pages and folders.</strong>
          They cost crawl budget and then sit in Issues as pages you will never work on, so they are
          <strong>excluded by default</strong>. Tick <em>Track</em> to crawl one anyway.
        </div>
        <div class="sitemap-controls-row never-track-summary-row">
          <span class="never-track-summary">
            <strong>{{ neverTrackExcludedCount | number }}</strong> URL(s) excluded from this crawl
          </span>
          <button type="button" class="btn btn--link" (click)="setAllNeverTrackTracked(true)">Track all</button>
          <button type="button" class="btn btn--link" (click)="setAllNeverTrackTracked(false)">Exclude all</button>
        </div>
        <div class="sitemap-list">
          <div
            class="sitemap-item"
            *ngFor="let group of neverTrackGroups; trackBy: trackNeverTrackGroup"
            [class.sitemap-item-disabled]="!isNeverTrackTracked(group)">
            <div class="sitemap-item-header">
              <label class="checkline">
                <input
                  type="checkbox"
                  [checked]="isNeverTrackTracked(group)"
                  (change)="toggleNeverTrackGroup(group, $any($event.target).checked)" />
                <span class="sitemap-url">{{ neverTrackGroupLabel(group) }}</span>
                <span class="sitemap-meta">({{ group.urlCount | number }} URLs · {{ group.categoryLabel }})</span>
                <span class="badge badge--pill" *ngIf="!isNeverTrackTracked(group)">Excluded</span>
                <span class="badge badge--pill badge--success" *ngIf="isNeverTrackTracked(group)">Tracked</span>
              </label>
            </div>
            <div class="never-track-reason">
              {{ group.reason }}
              <span *ngIf="!group.defaultExcluded" class="never-track-guard">
                Kept by default — this folder is most of the site, so we won't drop it unless you say so.
              </span>
            </div>
            <div class="sitemap-examples" *ngIf="(group.exampleUrls || []).length > 0">
              <span class="sitemap-examples-title">Examples:</span>
              <a
                *ngFor="let exampleUrl of (group.exampleUrls || []).slice(0, 2)"
                class="sitemap-example-link"
                [href]="exampleUrl"
                target="_blank"
                rel="noopener noreferrer">
                {{ exampleUrl }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="(est.localeGroups || []).length > 0" class="sitemap-controls locale-controls">
        <div class="locale-warning" [class.locale-warning-strong]="est.localeDetectionConfidence === 'heuristic'">
          <strong>Multiple languages/locales detected.</strong>
          <span *ngIf="est.localeDetectionConfidence === 'heuristic'">
            We detected these by URL pattern (this site doesn't split locales into separate sitemaps), so this is a best guess — review before crawling.
          </span>
          <span *ngIf="est.localeDetectionConfidence !== 'heuristic'">
            We detected these from the sitemap's hreflang annotations.
          </span>
          <span *ngIf="est.localePartitionedSitemaps">
            Leaf sitemaps are already split by language — non-selected locale sitemaps are unchecked automatically.
          </span>
          <strong>Only the checked locales are crawled — unchecked ones (greyed out) are excluded.</strong>
          By default we crawl just the default/no-locale pages.
        </div>
        <div class="sitemap-controls-row">
          <label class="checkline">
            <input type="checkbox" [checked]="areAllLocalesSelected()" (change)="setAllLocalesSelected($any($event.target).checked)" />
            Select all locales
          </label>
        </div>
        <div class="sitemap-list">
          <div
            class="sitemap-item"
            *ngFor="let group of est.localeGroups; trackBy: trackLocaleGroup"
            [class.sitemap-item-disabled]="!selectedLocales.has(group.key)">
            <div class="sitemap-item-header">
              <label class="checkline">
                <input
                  type="checkbox"
                  [checked]="selectedLocales.has(group.key)"
                  (change)="toggleLocaleSelection(group.key, $any($event.target).checked)" />
                <span class="sitemap-url">{{ group.label }}</span>
                <span class="sitemap-meta">({{ group.urlCount | number }} URLs)</span>
                <span class="badge badge--pill" *ngIf="!selectedLocales.has(group.key)">Excluded</span>
              </label>
            </div>
            <div class="sitemap-examples" *ngIf="(group.exampleUrls || []).length > 0">
              <span class="sitemap-examples-title">Examples:</span>
              <a
                *ngFor="let exampleUrl of (group.exampleUrls || []).slice(0, 2)"
                class="sitemap-example-link"
                [href]="exampleUrl"
                target="_blank"
                rel="noopener noreferrer">
                {{ exampleUrl }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- No domain selected — focal + optional competitors before any crawl -->
  <section class="card card--pad setup-empty" *ngIf="!domain && !showAddDomain">
    <h2>Start a new domain setup</h2>
    <p class="muted">
      Enter the focal domain and any competitors you want compared. You will get a prep list —
      check sitemaps, start all crawls, then walk away. For locale/folder scoping on one host,
      use <strong>+ Add domain</strong> above. Already crawled? Pick it from the Domain dropdown.
    </p>
    <div class="cold-start-builder">
      <label class="cold-start-label" for="cold-start-focal">Focal domain</label>
      <input
        id="cold-start-focal"
        type="text"
        class="input"
        placeholder="example.com"
        [(ngModel)]="coldStartFocalInput"
        (keydown.enter)="buildColdStartPrepList()" />
      <label class="cold-start-label" for="cold-start-competitors">Competitors (optional)</label>
      <textarea
        id="cold-start-competitors"
        rows="3"
        class="input"
        placeholder="One domain per line"
        [(ngModel)]="coldStartCompetitorsInput"></textarea>
      <button
        type="button"
        class="btn btn--accent"
        (click)="buildColdStartPrepList()"
        [disabled]="!coldStartFocalInput.trim()">
        Build prep list
      </button>
    </div>
  </section>

  <ng-container *ngIf="domain">
    <ng-container *ngIf="isPrepScopingPhase">
    <!-- Scoping-first: leaf selection + Start all before the pipeline UI -->
    <section class="setup-progress card setup-scoping-guide" *ngIf="setupGuide as guide">
      <div class="setup-guide" [attr.data-stage]="guide.stage">
        <div class="guide-head">
          <span class="guide-heading">{{ guide.heading }}</span>
          <span class="guide-eta" *ngIf="guide.eta">{{ guide.eta }}</span>
        </div>
        <p class="guide-detail">{{ guide.detail }}</p>
        <p class="guide-next" *ngIf="guide.action">Next: <strong>{{ guide.action }}</strong></p>
      </div>
    </section>

    <!-- Prep all domains (focal + competitors) -->
    <section class="steps card card--pad prep-all" id="setup-prep-all">
      <div class="competitors-head">
        <div>
          <h2>Prep all domains</h2>
          <p class="muted">
            Check sitemaps, pick leaf sitemaps / locales, then <strong>Start all crawls</strong>.
            Pipeline steps and Finish setup appear once a crawl is running.
          </p>
        </div>
        <div class="competitors-actions">
          <button
            type="button"
            class="btn btn--sm"
            (click)="checkAllPrepSitemaps()"
            [disabled]="checkingAllPrep || prepRows.length === 0">
            {{ checkingAllPrep ? ('Checking… (' + prepSitemapCheckDone + '/' + prepSitemapCheckTotal + ')') : 'Check all sitemaps' }}
          </button>
          <button
            type="button"
            class="btn btn--accent btn--sm"
            (click)="startAllPrepCrawls()"
            [disabled]="!canStartAllPrepCrawls"
            [title]="startAllPrepBlockedReason || 'Start crawls for every checked domain'">
            {{ startingAllPrep ? 'Starting…' : 'Start all crawls' }}
          </button>
        </div>
      </div>

      <p class="step-note step-note-warn" *ngIf="startAllPrepBlockedReason && !startingAllPrep && !checkingAllPrep">
        {{ startAllPrepBlockedReason }}
      </p>

      <div class="paid-enrichments prep-paid-enrichments">
        <div class="paid-head">
          <span class="paid-title">Paid enrichments</span>
          <span class="paid-note">Optional. Confirm once when you Start all crawls — then they run after core setup.</span>
        </div>
        <div class="optin-toggles">
          <label>
            <input type="checkbox" [(ngModel)]="includeKeywords" />
            Est. keywords (Labs cost)
          </label>
          <label>
            <input type="checkbox" [(ngModel)]="includeUrlSerp" />
            URL SERP snapshots (Labs cost; warms answer-structure LLM)
          </label>
          <label>
            <input
              type="checkbox"
              [(ngModel)]="includeTopSerpRivals"
              (ngModelChange)="onIncludeTopSerpRivalsChange($event)" />
            Top SERP rivals (crawl best rival + rival keywords — needs URL SERP)
          </label>
          <label>
            <input type="checkbox" [(ngModel)]="includeOpportunities" />
            Supporting-page opportunities (needs sidecar)
          </label>
        </div>
      </div>

      <div class="prep-batch-add">
        <textarea
          class="input"
          rows="3"
          placeholder="Add competitor domains (one per line)"
          [(ngModel)]="prepBatchInput"></textarea>
        <button type="button" class="btn btn--sm" (click)="addPrepDomainsFromBatchInput()" [disabled]="!prepBatchInput.trim()">
          Add to list
        </button>
      </div>

      <p class="step-note" *ngIf="showTopicPrepAllPrompt">
        All crawls finished — topic prep queues itself within a few seconds. If the chips below stay on
        <strong>Needs topic prep</strong>, run <strong>Topic prep for all</strong> manually.
      </p>

      <div class="step-row prep-domain-row" *ngFor="let row of prepRows">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ row.domain }}</span>
            <span class="badge badge--muted" *ngIf="row.role === 'focal'">Focal</span>
            <span class="badge" [ngClass]="prepRowChip(row).cls">{{ prepRowChip(row).label }}</span>
          </div>
          <p class="step-detail" *ngIf="row.estimateLoading">Checking sitemap…</p>
          <p class="step-detail" *ngIf="row.estimate as est">
            {{ est.selectedFilteredPages | number }} pages selected · est. ${{ est.selectedEstimatedTotalCost | number: '1.2-2' }}
            <span *ngIf="(est.neverTrackExcludedCount || 0) > 0" class="never-track-row-note">
              · {{ est.neverTrackExcludedCount | number }} low-value URL(s) excluded
            </span>
          </p>
          <p class="step-detail" *ngIf="!row.estimate && !row.estimateLoading && !row.estimateError">Run Check all sitemaps for page counts.</p>
          <p class="step-note step-note-warn" *ngIf="row.estimateError">{{ row.estimateError }}</p>
        </div>
        <div class="step-action">
          <button
            type="button"
            class="btn btn--sm"
            *ngIf="row.estimate"
            (click)="togglePrepRowExpanded(row)">
            {{ row.expanded ? 'Hide sitemaps' : 'Show sitemaps' }}
          </button>
          <button type="button" class="btn btn--sm" (click)="openPrepRowScope(row)" title="Full panel — upload / bot-protected sites">
            Scope
          </button>
        </div>

        <div class="prep-row-scope" *ngIf="row.expanded && row.estimate as est">
          <div class="estimate-summary">
            <p *ngIf="est.aliasApplied && est.canonicalDomain" class="estimate-line alias-note">
              Canonical domain: <strong>{{ est.canonicalDomain }}</strong>
              <span *ngIf="est.requestedDomain"> (from {{ est.requestedDomain }})</span>
            </p>
            <div class="estimate-grid">
              <span><span class="estimate-label">Discovered URLs:</span> {{ est.totalPagesInSitemap | number }}<span *ngIf="est.sitemapParseTruncated" class="estimate-warn"> (parse truncated)</span></span>
              <span><span class="estimate-label">Selected URLs:</span> {{ est.selectedPagesInSitemap | number }}</span>
              <span><span class="estimate-label">Included at cap ({{ est.limit }}):</span> {{ est.selectedFilteredPages | number }}</span>
              <span *ngIf="prepNeverTrackExcludedCount(row) > 0">
                <span class="estimate-label">Low-value URLs excluded:</span> {{ prepNeverTrackExcludedCount(row) | number }}
              </span>
              <span><span class="estimate-label">Est. crawl cost:</span> ${{ est.selectedEstimatedTotalCost | number: '1.2-2' }}</span>
            </div>

            <div *ngIf="(est.duplicateUrlsInSitemaps || 0) > 0" class="estimate-note">
              Duplicate URLs exist across sitemaps ({{ est.duplicateUrlsInSitemaps | number }}). Each unique URL is crawled once.
            </div>
            <div *ngIf="(est.sitemapParseFailures || []).length > 0" class="estimate-note warn">
              Parsed with warnings: {{ est.sitemapParseFailures?.length }} sitemap(s) failed to parse.
            </div>

            <div *ngIf="getLeafNodes(est.sitemapTree).length > 0" class="sitemap-controls">
              <div class="sitemap-controls-row">
                <label class="checkline">
                  <input
                    type="checkbox"
                    [checked]="areAllPrepSitemapsSelected(row)"
                    (change)="setAllPrepSitemapsSelected(row, $any($event.target).checked)" />
                  Select all leaf sitemaps
                </label>
              </div>
              <div class="sitemap-filter-row">
                <label class="strategy-label">
                  Lastmod on/after:
                  <input
                    type="date"
                    class="input"
                    [ngModel]="row.minLastmodFrom"
                    (ngModelChange)="onPrepMinLastmodFromChange(row, $event)" />
                </label>
                <label class="strategy-label">
                  Per-sitemap cap mode:
                  <select
                    class="input"
                    [ngModel]="row.perSitemapLimitMode"
                    (ngModelChange)="onPrepPerSitemapLimitModeChange(row, $event)">
                    <option value="recent">Most recent first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="balanced">Balanced (newest + oldest)</option>
                  </select>
                </label>
              </div>
              <div class="sitemap-filter-note">Per-sitemap cap applies after lastmod filtering using the selected mode.</div>

              <div class="sitemap-list">
                <div
                  class="sitemap-item"
                  *ngFor="let leaf of getLeafNodes(est.sitemapTree); trackBy: trackSitemap"
                  [class.sitemap-item-disabled]="!row.selectedSitemaps.has(leaf.sitemapUrl)">
                  <div class="sitemap-item-header">
                    <label class="checkline">
                      <input
                        type="checkbox"
                        [checked]="row.selectedSitemaps.has(leaf.sitemapUrl)"
                        (change)="togglePrepSitemap(row, leaf.sitemapUrl, $any($event.target).checked)" />
                      <span class="sitemap-url">{{ displaySitemapLabel(leaf.sitemapUrl) }}</span>
                      <span class="sitemap-meta">({{ getPrepLeafDisplayUrlCount(row, leaf) | number }} URLs)</span>
                    </label>
                    <label class="leaf-limit-label">
                      Crawl
                      <input
                        type="number"
                        min="1"
                        step="1"
                        class="input leaf-limit-input"
                        [ngModel]="getPrepPerSitemapLimit(row, leaf.sitemapUrl)"
                        (ngModelChange)="onPrepPerSitemapLimitChange(row, leaf.sitemapUrl, $event)"
                        placeholder="all" />
                      URLs
                    </label>
                  </div>
                  <div class="sitemap-examples" *ngIf="(leaf.exampleUrls || []).length > 0">
                    <span class="sitemap-examples-title">Examples:</span>
                    <a
                      *ngFor="let exampleUrl of (leaf.exampleUrls || []).slice(0, 2)"
                      class="sitemap-example-link"
                      [href]="exampleUrl"
                      target="_blank"
                      rel="noopener noreferrer">
                      {{ exampleUrl }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div *ngIf="(est.neverTrackGroups || []).length > 0" class="sitemap-controls never-track-controls">
              <div class="never-track-warning">
                <strong>We found low-SEO-value pages and folders.</strong>
                They cost crawl budget and then sit in Issues as pages you will never work on, so they are
                <strong>excluded by default</strong>. Tick <em>Track</em> to crawl one anyway.
              </div>
              <div class="sitemap-controls-row never-track-summary-row">
                <span class="never-track-summary">
                  <strong>{{ prepNeverTrackExcludedCount(row) | number }}</strong> URL(s) excluded from this crawl
                </span>
                <button type="button" class="btn btn--link" (click)="setAllPrepNeverTrackTracked(row, true)">Track all</button>
                <button type="button" class="btn btn--link" (click)="setAllPrepNeverTrackTracked(row, false)">Exclude all</button>
              </div>
              <div class="sitemap-list">
                <div
                  class="sitemap-item"
                  *ngFor="let group of est.neverTrackGroups; trackBy: trackNeverTrackGroup"
                  [class.sitemap-item-disabled]="!isPrepNeverTrackTracked(row, group)">
                  <div class="sitemap-item-header">
                    <label class="checkline">
                      <input
                        type="checkbox"
                        [checked]="isPrepNeverTrackTracked(row, group)"
                        (change)="togglePrepNeverTrack(row, group, $any($event.target).checked)" />
                      <span class="sitemap-url">{{ neverTrackGroupLabel(group) }}</span>
                      <span class="sitemap-meta">({{ group.urlCount | number }} URLs · {{ group.categoryLabel }})</span>
                      <span class="badge badge--pill" *ngIf="!isPrepNeverTrackTracked(row, group)">Excluded</span>
                      <span class="badge badge--pill badge--success" *ngIf="isPrepNeverTrackTracked(row, group)">Tracked</span>
                    </label>
                  </div>
                  <div class="never-track-reason">
                    {{ group.reason }}
                    <span *ngIf="!group.defaultExcluded" class="never-track-guard">
                      Kept by default — this folder is most of the site, so we won't drop it unless you say so.
                    </span>
                  </div>
                  <div class="sitemap-examples" *ngIf="(group.exampleUrls || []).length > 0">
                    <span class="sitemap-examples-title">Examples:</span>
                    <a
                      *ngFor="let exampleUrl of (group.exampleUrls || []).slice(0, 2)"
                      class="sitemap-example-link"
                      [href]="exampleUrl"
                      target="_blank"
                      rel="noopener noreferrer">
                      {{ exampleUrl }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div *ngIf="(est.localeGroups || []).length > 0" class="sitemap-controls locale-controls">
              <div class="locale-warning" [class.locale-warning-strong]="est.localeDetectionConfidence === 'heuristic'">
                <strong>Multiple languages/locales detected.</strong>
                <span *ngIf="est.localeDetectionConfidence === 'heuristic'">
                  We detected these by URL pattern (this site doesn't split locales into separate sitemaps), so this is a best guess — review before crawling.
                </span>
                <span *ngIf="est.localeDetectionConfidence !== 'heuristic'">
                  We detected these from the sitemap's hreflang annotations.
                </span>
                <span *ngIf="row.localePartitionedSitemaps">
                  Leaf sitemaps are already split by language — non-selected locale sitemaps are unchecked automatically.
                </span>
                <strong>Only the checked locales are crawled — unchecked ones (greyed out) are excluded.</strong>
                By default we crawl just the default/no-locale pages.
              </div>
              <div class="sitemap-controls-row">
                <label class="checkline">
                  <input
                    type="checkbox"
                    [checked]="areAllPrepLocalesSelected(row)"
                    (change)="setAllPrepLocalesSelected(row, $any($event.target).checked)" />
                  Select all locales
                </label>
              </div>
              <div class="sitemap-list">
                <div
                  class="sitemap-item"
                  *ngFor="let group of est.localeGroups; trackBy: trackLocaleGroup"
                  [class.sitemap-item-disabled]="!row.selectedLocales.has(group.key)">
                  <div class="sitemap-item-header">
                    <label class="checkline">
                      <input
                        type="checkbox"
                        [checked]="row.selectedLocales.has(group.key)"
                        (change)="togglePrepLocale(row, group.key, $any($event.target).checked)" />
                      <span class="sitemap-url">{{ group.label }}</span>
                      <span class="sitemap-meta">({{ group.urlCount | number }} URLs)</span>
                      <span class="badge badge--pill" *ngIf="!row.selectedLocales.has(group.key)">Excluded</span>
                    </label>
                  </div>
                  <div class="sitemap-examples" *ngIf="(group.exampleUrls || []).length > 0">
                    <span class="sitemap-examples-title">Examples:</span>
                    <a
                      *ngFor="let exampleUrl of (group.exampleUrls || []).slice(0, 2)"
                      class="sitemap-example-link"
                      [href]="exampleUrl"
                      target="_blank"
                      rel="noopener noreferrer">
                      {{ exampleUrl }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="muted" *ngIf="prepRows.length === 0">Add competitors above or use <strong>+ Add competitor</strong> for full scoping controls.</p>
    </section>
    </ng-container>

    <ng-container *ngIf="!isPrepScopingPhase">
    <!-- Progress + Finish setup -->
    <section class="setup-progress card card--pad">
      <!-- "You are here" (SS001-9): one line for stage, one for what to do next. -->
      <div class="setup-guide" [attr.data-stage]="setupGuide.stage" *ngIf="setupGuide as guide">
        <div class="guide-head">
          <span class="guide-heading">{{ guide.heading }}</span>
          <span class="guide-eta" *ngIf="guide.eta">{{ guide.eta }}</span>
        </div>
        <p class="guide-detail">{{ guide.detail }}</p>
        <p class="guide-next" *ngIf="guide.action">Next: <strong>{{ guide.action }}</strong></p>
        <p class="guide-next guide-next-auto" *ngIf="!guide.action">Nothing to click — this step runs on its own.</p>
      </div>

      <div class="progress-row">
        <div class="progress-meta">
          <span class="progress-domain">{{ domain }}</span>
          <span class="progress-count">{{ completeCount }} / {{ totalCount }} steps complete</span>
        </div>
        <div class="progress-actions">
          <button type="button" class="btn" (click)="loadAll()" [disabled]="loading">Refresh</button>
          <button
            type="button"
            class="btn btn--accent"
            (click)="finishSetup()"
            [disabled]="runningAll || !!finishSetupBlockedReason"
            [title]="finishSetupBlockedReason || 'Re-run the core pipeline. It also queues itself once every domain is prepped — no paid steps.'">
            {{ runningAll ? 'Queuing…' : 'Finish setup' }}
          </button>
        </div>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" [style.width.%]="progressPercent"></div>
      </div>
      <p class="progress-blocked" *ngIf="finishSetupBlockedReason">{{ finishSetupBlockedReason }}</p>
      <p class="step-note" *ngIf="crawlStatus?.durationMs as dur">
        Last crawl completed in {{ formatDurationMs(dur) }}<span *ngIf="crawlStatus?.crawlMode"> ({{ crawlStatus?.crawlMode }} mode)</span>.
      </p>
      <p class="step-note step-note-warn" *ngIf="crawlStatus?.errorMessage">
        {{ crawlStatus?.errorMessage }}
      </p>
      <!-- Undercrawl (SS001-10). Severe and ordinary attrition read completely
           differently, so they do not share a sentence. -->
      <ng-container *ngIf="coverageShortfall as gap">
        <div class="coverage-warning" *ngIf="gap.severity === 'severe'">
          <div class="coverage-head">
            <span class="badge state-undercrawled">Undercrawled</span>
            <span class="coverage-counts">{{ undercrawlCounts(gap) }}</span>
          </div>
          <p class="coverage-detail">
            {{ gap.missing | number }} of the URLs you scoped never became a page analysis. Topics, clusters
            and supporting-page comparisons for <strong>{{ gap.domain }}</strong> are built only on the
            {{ gap.analyzed | number }} that did — read them as a sample of the site, not the site.
          </p>
          <p class="coverage-examples" *ngIf="gap.examples.length">
            Missing, for example: <span *ngFor="let u of gap.examples; last as isLast">{{ u }}{{ isLast ? '' : ' · ' }}</span>
          </p>
          <p class="coverage-detail">
            Nothing re-crawls on its own. If the gap matters, re-scope the sitemap (locale folders are the
            usual cause) and crawl again from <strong>Prep all domains</strong> — otherwise carry on knowing
            the numbers are partial.
          </p>
        </div>
        <p class="step-note" *ngIf="gap.severity === 'gap'">
          Coverage gap: {{ gap.analyzed | number }} of {{ gap.expected | number }} pages analyzed.
          {{ gap.missing | number }} scoped URL(s) never became a page analysis — normally redirects, 404s or
          noindex. No action needed unless you expected those pages.
        </p>
      </ng-container>

      <!--
        Paid steps (SS001-11 / GO_LIVE_PLAN Track A). Opt-in + confirm happen on
        the prep screen at Start all; this is status only. Re-run via enrichment
        row buttons below if needed.
      -->
      <div class="paid-enrichments" *ngIf="hasPendingPaidEnrichments || selectedPaidEnrichments.length">
        <div class="paid-head">
          <span class="paid-title">Paid enrichments</span>
          <span class="paid-note" *ngIf="hasPendingPaidEnrichments">
            Confirmed at Start all — running as prerequisites finish
            <span *ngIf="includeKeywords"> · keywords</span><span *ngIf="includeUrlSerp || includeTopSerpRivals"> · URL SERP</span><span *ngIf="includeTopSerpRivals"> · top SERP rivals</span><span *ngIf="includeOpportunities"> · opportunities</span>.
          </span>
          <span class="paid-note" *ngIf="!hasPendingPaidEnrichments && selectedPaidEnrichments.length">
            Opted in at Start all. Use Run on the enrichment rows below to re-run.
          </span>
        </div>
      </div>

      <div class="topics-map-scope" *ngIf="isDevView()">
        <div class="scope-head">
          <span class="scope-title">Dev: Topic map scope</span>
        </div>
        <div class="scope-options">
          <label>
            <input
              type="radio"
              name="topicsMapScope"
              value="all"
              [checked]="topicsMapScope === 'all'"
              (change)="onTopicsMapScopeChange('all')" />
            All domains (default)
          </label>
          <label>
            <input
              type="radio"
              name="topicsMapScope"
              value="selected"
              [checked]="topicsMapScope === 'selected'"
              (change)="onTopicsMapScopeChange('selected')" />
            Selected domains only
          </label>
        </div>

        <div class="scope-domains" *ngIf="topicsMapScope === 'selected'">
          <label *ngFor="let d of topicsMapCandidates" class="scope-domain">
            <input
              type="checkbox"
              [checked]="isTopicsMapDomainSelected(d)"
              (change)="toggleTopicsMapDomain(d)" />
            {{ d }}
          </label>
          <p class="step-note-warn">
            Dev only — rebuilds the workspace topic map from just these domains.
            All other domains' topic mappings are cleared until a full Topics map runs.
          </p>
        </div>
      </div>
    </section>

    <p class="error" *ngIf="error">{{ error }}</p>

    <!-- Readiness for Inventory / Action Plan -->
    <section class="readiness card card--pad" *ngIf="analysisComplete">
      <div class="readiness-head">
        <h2>Ready to review?</h2>
        <span class="badge" [ngClass]="reportReady ? 'state-complete' : 'state-partial'">
          {{ reportReady ? 'Clear for work items' : 'Not yet' }}
        </span>
      </div>
      <p class="muted" *ngIf="reportReady">
        Core pipeline and page demand labels are done. Open <strong>Inventory → Pages</strong> or <strong>Action Plan</strong> to review issues and work items.
        <span *ngIf="enrichmentGaps.length"> Optional enrichments still pending below.</span>
      </p>
      <ng-container *ngIf="!reportReady">
        <p class="muted">Finish these before trusting cluster organization, health bands, and action-plan work items:</p>
        <ul class="readiness-list">
          <li *ngFor="let item of reportReadyBlockers">{{ item }}</li>
        </ul>
      </ng-container>
      <ul class="readiness-gaps" *ngIf="reportReady && enrichmentGaps.length">
        <li *ngFor="let gap of enrichmentGaps">{{ gap }}</li>
      </ul>
      <!-- SS001-10. Ready is not the same as complete: a domain here can be clear for
           work items and still have been crawled in part. Say which, and what it costs. -->
      <div class="coverage-warning" *ngIf="severeUndercrawls.length">
        <div class="coverage-head">
          <span class="badge state-undercrawled">Undercrawled</span>
          <span class="coverage-counts">{{ severeUndercrawls.length }} domain(s) crawled in part</span>
        </div>
        <ul class="readiness-gaps">
          <li *ngFor="let gap of severeUndercrawls">
            <strong>{{ gap.domain }}</strong> — {{ undercrawlCounts(gap) }}.
            <span *ngIf="gap.domain !== domain">
              Supporting-page comparisons against it are built on part of its site, so its coverage is
              understated and gaps you see against it may not be real.
            </span>
            <span *ngIf="gap.domain === domain">
              Your own topics, clusters and health bands come from that subset.
            </span>
          </li>
        </ul>
      </div>
    </section>

    <!-- Core checklist -->
    <section class="steps card card--pad">
      <h2>Pipeline</h2>
      <div class="step-row" *ngFor="let step of coreSteps">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ step.title }}</span>
            <span class="badge" [ngClass]="stateClass(step.state)">{{ stateLabel(step.state) }}</span>
            <span class="badge state-undercrawled" *ngIf="step.warningChip">{{ step.warningChip }}</span>
          </div>
          <p class="step-desc">{{ step.description }}</p>
          <p class="step-detail" *ngIf="step.detail">{{ step.detail }}</p>
          <p class="step-note" *ngIf="step.note">{{ step.note }}</p>
          <p class="step-note step-note-warn" *ngIf="step.warning">{{ step.warning }}</p>
          <p class="step-blocked" *ngIf="step.state === 'blocked' && step.blockedReason">{{ step.blockedReason }}</p>
        </div>
        <div class="step-action" *ngIf="step.actionable">
          <button
            type="button"
            class="btn btn--sm"
            (click)="runStep(step)"
            [disabled]="isStepActionDisabled(step)"
            [title]="stepActionDisabledReason(step) || step.actionLabel">
            {{ isBusy(step.key) ? 'Starting…' : step.actionLabel }}
          </button>
        </div>
      </div>
    </section>

    <!-- Enrichment (parity with Inventory dev refresh buttons) -->
    <section class="steps card card--pad">
      <h2>Enrichment &amp; health</h2>
      <p class="muted">
        Same jobs as Inventory dev refresh buttons — run status is shown here so you know what has been computed.
      </p>
      <div class="step-row" *ngFor="let step of enrichmentSteps">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ step.title }}</span>
            <span class="badge" [ngClass]="stateClass(step.state)">{{ stateLabel(step.state) }}</span>
            <span class="badge state-undercrawled" *ngIf="step.warningChip">{{ step.warningChip }}</span>
          </div>
          <p class="step-desc">{{ step.description }}</p>
          <p class="step-detail" *ngIf="step.detail">{{ step.detail }}</p>
          <p class="step-note" *ngIf="step.note">{{ step.note }}</p>
          <p class="step-note step-note-warn" *ngIf="step.warning">{{ step.warning }}</p>
          <div class="url-serp-progress" *ngIf="step.key === 'url-serp' && urlSerpProgress">
            <div class="progress-bar compact">
              <div class="progress-fill" [style.width.%]="urlSerpProgress.percent"></div>
            </div>
            <p class="step-detail">{{ urlSerpProgress.statusLine }}</p>
          </div>
          <label class="force-api" *ngIf="step.key === 'url-serp'">
            <input type="checkbox" [(ngModel)]="urlSerpForceRefresh" />
            Force API (bypass SERP cache)
          </label>
        </div>
        <div class="step-action" *ngIf="step.actionable">
          <button
            type="button"
            class="btn btn--sm"
            (click)="runStep(step)"
            [disabled]="isStepActionDisabled(step) || (step.key === 'url-serp' && isBusy('url-serp'))"
            [title]="stepActionDisabledReason(step) || step.actionLabel">
            {{ step.key === 'url-serp' && isBusy('url-serp') ? 'Running…' : (isBusy(step.key) ? 'Starting…' : step.actionLabel) }}
          </button>
        </div>
      </div>
    </section>

    <!-- Search Console (GSC_INTEGRATION.md stage 1): renders nothing unless this site is connected. -->
    <app-gsc-connection [domain]="domain"></app-gsc-connection>

    <!-- Opt-in steps -->
    <section class="steps card card--pad">
      <h2>Optional (opt-in)</h2>
      <p class="muted">
        These cost money or time. They are never part of <em>Finish setup</em> — check them under
        <strong>Paid enrichments</strong> above and confirm, or run one here.
      </p>
      <div class="step-row" *ngFor="let step of optionalSteps">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ step.title }}</span>
            <span class="badge" [ngClass]="stateClass(step.state)">{{ stateLabel(step.state) }}</span>
            <span class="badge state-undercrawled" *ngIf="step.warningChip">{{ step.warningChip }}</span>
          </div>
          <p class="step-desc">{{ step.description }}</p>
          <p class="step-detail" *ngIf="step.detail">{{ step.detail }}</p>
          <p class="step-note" *ngIf="step.note">{{ step.note }}</p>
          <p class="step-note step-note-warn" *ngIf="step.warning">{{ step.warning }}</p>
          <p class="step-blocked" *ngIf="step.state === 'blocked' && opportunitiesBlockedSummary(step) as summary">
            {{ summary }}
          </p>
          <p class="step-note" *ngIf="step.key === 'supporting-opportunities' && status?.supportingPagesByTopic?.status === 'complete' && step.state === 'blocked'">
            Supporting pages by topic is already done — you do <strong>not</strong> need to run it again.
          </p>
        </div>
        <div class="step-action" *ngIf="step.actionable">
          <button
            type="button"
            class="btn btn--sm"
            (click)="runStep(step)"
            [disabled]="isStepActionDisabled(step)"
            [title]="stepActionDisabledReason(step) || step.actionLabel">
            {{ isBusy(step.key) ? 'Starting…' : step.actionLabel }}
          </button>
        </div>
      </div>
    </section>

    <!-- Competitors -->
    <section class="steps card competitors-card">
      <div class="competitors-head">
        <div>
          <h2>Competitors</h2>
          <p class="muted">
            Competitors of <strong>{{ domain }}</strong>. They need a crawl + minimal topic prep to feed this domain's
            supporting-page comparisons. Topic prep is queued for you once every domain in the prep list has finished
            crawling — the button is a manual re-run.
          </p>
        </div>
        <div class="competitors-actions">
          <button
            type="button"
            class="btn btn--sm"
            (click)="runCompetitorPrepAll()"
            [disabled]="runningCompetitorPrepAll || !hasPreppableCompetitors"
            title="Re-run topic prep for all crawled competitors (topics-map runs once for the batch). Normally queued automatically when the last crawl finishes.">
            {{ runningCompetitorPrepAll ? 'Queuing…' : 'Run topic prep for all' }}
          </button>
          <button type="button" class="btn btn--accent btn--sm" (click)="openAddDomain('competitor')">+ Add competitor</button>
        </div>
      </div>

      <!-- Suggested from SERPs -->
      <div class="serp-suggestions" *ngIf="serpSuggestionsLoading || serpSuggestions.length || serpSuggestionsError">
        <div class="serp-suggestions-head">
          <span class="serp-suggestions-title">Suggested from SERPs</span>
          <span class="muted serp-suggestions-sub">
            Domains ranking across {{ serpSuggestionsMinTrackedUrls }}+ of your pages' SERPs. Add crawls only their ranking pages.
          </span>
        </div>
        <p class="muted" *ngIf="serpSuggestionsLoading">Loading SERP suggestions…</p>
        <p class="step-note step-note-warn" *ngIf="serpSuggestionsError && !serpSuggestionsLoading">{{ serpSuggestionsError }}</p>
        <div class="serp-suggestion-row" *ngFor="let s of serpSuggestions">
          <div class="serp-suggestion-main">
            <div class="step-head">
              <span class="step-title">{{ s.domain }}</span>
              <span class="badge" *ngIf="isSuggestionAlreadyCompetitor(s.domain)">Already a competitor</span>
            </div>
            <p class="step-detail">
              Ranks on {{ s.appearsOnUrlCount | number }} of your pages · {{ s.urlCount | number }} ranking page(s)<span *ngIf="s.avgPosition !== null"> · avg #{{ s.avgPosition }}</span>
            </p>
            <p class="step-note" *ngIf="s.sampleQueries?.length">e.g. {{ s.sampleQueries[0] }}</p>
          </div>
          <div class="step-action">
            <button
              type="button"
              class="btn btn--sm"
              (click)="addSerpSuggestionAsCompetitor(s)"
              [disabled]="isSuggestionAlreadyCompetitor(s.domain) || !s.urlCount">
              Add with SERP pages
            </button>
          </div>
        </div>
      </div>

      <p class="muted" *ngIf="competitors.length === 0">No competitors marked yet. Add one above, or mark an already-crawled domain below.</p>

      <div class="step-row" *ngFor="let c of competitors">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ c.domain }}</span>
            <span class="badge" [ngClass]="competitorChip(c).cls">{{ competitorChip(c).label }}</span>
            <span class="badge state-undercrawled" *ngIf="severeShortfallFor(c.domain)">Undercrawled</span>
          </div>
          <p class="step-detail">{{ c.pagesAnalyzed | number }} pages analyzed · {{ c.aggregateCount | number }} topics</p>
          <!-- SS001-10. The comparison, not the crawl, is what the operator loses here. -->
          <ng-container *ngIf="severeShortfallFor(c.domain) as gap">
            <p class="step-note step-note-warn">
              Crawled in part — {{ undercrawlCounts(gap) }}. Its topic set covers that subset only, so
              supporting-page comparisons will understate what this competitor actually has.
            </p>
          </ng-container>
          <p class="step-note step-note-warn" *ngIf="competitorContentBlocked(c)">
            No page content was captured — the site likely renders content client-side or blocks crawlers, so no topic signals could be extracted. Re-crawl to retry; if it persists, this domain can’t be prepped via the standard pipeline.
          </p>
          <p class="step-note" *ngIf="competitorCrawlInFlight(c)">
            Crawl in progress — this is the same crawl shown in <strong>Prep all domains</strong> above. Topic prep unlocks when it finishes.
          </p>
          <p class="step-note" *ngIf="!competitorCrawlInFlight(c) && c.crawled && !c.hasTopicSignals && !competitorContentBlocked(c)">
            Crawl finished — extracting topic signals. Topic prep unlocks once they exist.
          </p>
          <p class="step-note" *ngIf="!competitorCrawlInFlight(c) && !c.crawled">Not crawled yet in this workspace. Crawl it (via “Add competitor”) before running topic prep.</p>
        </div>
        <div class="step-action competitor-action">
          <button
            type="button"
            class="btn btn--sm"
            (click)="runCompetitorPrep(c)"
            [disabled]="isCompetitorBusy(c.domain) || !canPrepCompetitor(c)">
            {{ isCompetitorBusy(c.domain) ? 'Working…' : 'Run topic prep' }}
          </button>
          <button
            type="button"
            class="btn btn--link btn--sm"
            (click)="removeCompetitor(c)"
            [disabled]="isCompetitorBusy(c.domain)">
            Remove
          </button>
        </div>
      </div>

      <div class="mark-existing-row">
        <span class="mark-existing-label">Mark an already-crawled domain:</span>
        <input
          class="input"
          type="text"
          placeholder="competitor.com"
          [(ngModel)]="markCompetitorInput"
          (keyup.enter)="markExistingCompetitor()" />
        <button type="button" class="btn btn--sm" (click)="markExistingCompetitor()" [disabled]="!markCompetitorInput">
          Mark competitor
        </button>
      </div>

      <p class="competitor-stale" *ngIf="competitorStatus?.staleForCompetitorSet">
        The competitor set changed since the last supporting-pages build — re-run “Supporting pages by topic” (and opportunities, if used) to include the current competitors.
      </p>
    </section>

    <!-- Top SERP rivals -->
    <section class="steps card top-serp-rivals-card">
      <div class="competitors-head">
        <div>
          <h2>Top SERP rivals</h2>
          <p class="muted">
            For each page a rival is outranking, crawl the single best-ranked competitor page from that page's SERP, precompute "what they cover that your page doesn't", and fetch that rival's ranked keywords — so the comparison is ready in Issues instead of doing it page by page.
          </p>
          <p class="muted">
            Opt in on the prep screen (Start all) for a one-confirm walk-away, or use the button below to re-run.
            Keyword fetch starts on its own when the crawl finishes.
          </p>
        </div>
        <div class="competitors-actions">
          <button
            type="button"
            class="btn btn--accent btn--sm"
            (click)="runTopSerpRivals()"
            [disabled]="!!rivalChainBlockedReason()"
            [title]="rivalChainBlockedReason() || 'Show the cost breakdown, then crawl rivals and fetch their keywords'">
            {{ topSerpRivalsRunning || topSerpRivalsBuilding() ? 'Building…' : 'Crawl rivals &amp; fetch keywords' }}
          </button>
        </div>
      </div>

      <p class="muted" *ngIf="topSerpRivalsLoading">Loading rival preview…</p>
      <p class="step-note step-note-warn" *ngIf="topSerpRivalsError && !topSerpRivalsLoading">{{ topSerpRivalsError }}</p>

      <ng-container *ngIf="topSerpRivals && !topSerpRivalsLoading">
        <p class="muted" *ngIf="topSerpRivals.candidateFocals === 0">
          No rivals to crawl — either the URL SERP step hasn't run yet, or you already rank top for your tracked pages.
        </p>

        <p class="step-note" *ngIf="answerStructureStatusLine() as asLine">
          {{ asLine }}
        </p>

        <!--
          SS001-15: the ceiling is part of the offer, not a surprise. "N of M"
          renders before the click, and the overflow is named as deferred —
          never a silent MAX_RIVALS-style truncation.
        -->
        <p class="step-note tsr-ceiling" [class.step-note-warn]="topSerpRivals.capped" *ngIf="rivalCeilingLabel() as ceilingLine">
          {{ ceilingLine }}
        </p>

        <div class="top-serp-rivals-cost" *ngIf="topSerpRivals.candidateFocals > 0">
          <div class="tsr-cost-line">
            <span class="tsr-cost-step">1. Crawl rivals</span>
            <span><strong>{{ topSerpRivals.trackedUrlsWithRival | number }}</strong> page(s) → <strong>{{ topSerpRivals.uniqueRivalCount | number }}</strong> unique rival(s)</span>
            <span>·</span>
            <span>
              <strong>{{ topSerpRivals.crawlsNeeded | number }}</strong> to crawl
              <span class="muted" *ngIf="topSerpRivals.alreadyAnalyzedCount > 0">({{ topSerpRivals.alreadyAnalyzedCount | number }} already analyzed — $0)</span>
            </span>
            <span>·</span>
            <span>est. <strong>${{ topSerpRivals.estimatedCrawlCost.toFixed(2) }}</strong></span>
          </div>
          <div class="tsr-cost-line">
            <span class="tsr-cost-step">2. Rival keywords</span>
            <span>up to <strong>{{ topSerpRivals.keywordFetchesNeeded | number }}</strong> Labs fetch(es)</span>
            <span class="muted" *ngIf="topSerpRivals.alreadyHaveKeywordsCount > 0">({{ topSerpRivals.alreadyHaveKeywordsCount | number }} already have keywords — $0)</span>
            <span>·</span>
            <span>est. <strong>${{ topSerpRivals.estimatedKeywordsCost.toFixed(2) }}</strong></span>
          </div>
          <div class="tsr-cost-line tsr-cost-total">
            <span class="tsr-cost-step">Estimated total</span>
            <span><strong>${{ topSerpRivals.estimatedTotalCost.toFixed(2) }}</strong></span>
            <button type="button" class="btn btn--link btn--sm" (click)="toggleTopSerpRivals()">
              {{ topSerpRivalsExpanded ? 'Hide list' : 'Show list' }}
            </button>
          </div>
        </div>

        <!-- Build progress / result -->
        <div class="top-serp-rivals-progress" *ngIf="topSerpRivalsStatus && topSerpRivalsStatus.status !== 'not_started'">
          <div class="tsr-progress-bar" *ngIf="topSerpRivalsBuilding()">
            <div class="tsr-progress-fill" [style.width.%]="topSerpRivalsProgressPct()"></div>
          </div>
          <p class="step-detail" *ngIf="topSerpRivalsBuilding()">
            {{ topSerpRivalsStatusLine() }}
            <span class="muted">({{ topSerpRivalsProgressPct() }}%)</span>
          </p>
          <p class="step-note" *ngIf="topSerpRivalsBuilding() && serpRivalKeywordsQueuedBehindCrawl()">
            Rival keywords are queued behind this crawl — they start on their own, no second click.
          </p>
          <p class="step-note" *ngIf="topSerpRivalsStatus.status === 'ready'">
            Coverage precomputed for {{ topSerpRivalsSuccessCount() | number }} page(s).
            <span class="step-note-warn" *ngIf="topSerpRivalsFailureCount() > 0">
              Couldn't process {{ topSerpRivalsFailureCount() | number }} page(s).
            </span>
          </p>

          <!--
            The crawl leg finishing is not the run finishing. Saying only
            "coverage precomputed" here would read as done while the keyword
            fetch the same confirm paid for is still going.
          -->
          <p class="step-note" *ngIf="topSerpRivalsStatus.status === 'ready' && topSerpRivalsStatus.chainKeywords && serpRivalKeywordsBuilding()">
            Still running — fetching rival ranked keywords now, same run.
          </p>

          <!-- SS001-7: actual duration + actual cost across both legs of the chain. -->
          <ng-container *ngIf="rivalChainActuals() as actuals">
            <p class="step-note tsr-actuals" *ngIf="actuals.complete">
              Done in <strong>{{ actuals.durationLabel || 'under a second' }}</strong>
              · billed <strong>{{ actuals.costLabel }}</strong>
              <span class="muted">
                ({{ topSerpRivalsStatus.crawlsBilled | number }} crawl(s)<span *ngIf="topSerpRivalsStatus.chainKeywords && serpRivalKeywordsStatus">, {{ serpRivalKeywordsStatus.fetchedUrls | number }} keyword fetch(es)</span>)
              </span>
            </p>
          </ng-container>
          <p class="step-note step-note-warn" *ngIf="topSerpRivalsStatus.status === 'ready' && !topSerpRivalsStatus.serviceAvailable">
            The content-gap service was unavailable, so section coverage couldn't be computed. Start it (npm run content-gap:serve) and re-run.
          </p>
          <p class="step-note step-note-warn" *ngIf="topSerpRivalsStatus.status === 'failed'">
            Build failed: {{ topSerpRivalsStatus.lastError || 'unknown error' }}
          </p>

          <!-- Per-focal failure list -->
          <div class="top-serp-rivals-failures" *ngIf="topSerpRivalsFailureCount() > 0">
            <button type="button" class="btn btn--link btn--sm" (click)="toggleTopSerpRivalsFailures()">
              {{ topSerpRivalsFailuresExpanded ? 'Hide' : 'Show' }} couldn't-process list ({{ topSerpRivalsFailureCount() | number }})
            </button>
            <div class="tsr-failures-list" *ngIf="topSerpRivalsFailuresExpanded">
              <div class="tsr-failure-row" *ngFor="let f of topSerpRivalsStatus.failures">
                <span class="tsr-path" [title]="f.focalUrl">{{ formatRivalUrl(f.focalUrl) }}</span>
                <span class="tsr-arrow">→</span>
                <span class="tsr-path" [title]="f.rivalUrl">{{ formatRivalUrl(f.rivalUrl) }}</span>
                <p class="tsr-query" *ngIf="f.query">for “{{ f.query }}”</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview list: focal page -> rival it loses to -->
        <div class="top-serp-rivals-list" *ngIf="topSerpRivalsExpanded && topSerpRivals.trackedUrlsWithRival > 0">
          <p class="muted tsr-list-note" *ngIf="topSerpRivals.capped">
            Showing the {{ topSerpRivals.trackedUrlsWithRival | number }} page(s) this run covers. The remaining
            {{ topSerpRivals.deferredFocals | number }} are reachable one at a time from each page in Issues.
          </p>
          <div class="tsr-row" *ngFor="let p of topSerpRivals.picks">
            <div class="tsr-focal">
              <span class="tsr-path" [title]="p.focalUrl">{{ formatRivalUrl(p.focalUrl) }}</span>
              <span class="tsr-pos" *ngIf="p.yourPosition !== null">you #{{ p.yourPosition }}</span>
              <span class="tsr-pos tsr-unranked" *ngIf="p.yourPosition === null">not ranked</span>
            </div>
            <div class="tsr-rival">
              <span class="tsr-arrow">vs</span>
              <span class="tsr-path" [title]="p.rivalUrl">{{ formatRivalUrl(p.rivalUrl) }}</span>
              <span class="tsr-pos tsr-rival-pos">#{{ p.rivalPosition }}</span>
            </div>
            <p class="tsr-query" *ngIf="p.query">for “{{ p.query }}”</p>
          </div>
        </div>
      </ng-container>

      <!-- Step 2: rival ranked keywords (Labs) -->
      <div class="top-serp-rivals-keywords" *ngIf="topSerpRivals && topSerpRivals.uniqueRivalCount > 0">
        <div class="competitors-head tsr-keywords-head">
          <div>
            <h3 class="tsr-keywords-title">Rival ranked keywords</h3>
            <p class="muted">
              Labs ranked keywords for each unique rival page, so Quick Compare shows keyword overlap without opening Deep Compare.
              <strong>Runs automatically</strong> as the second half of the button above — this is a re-run for pages it missed, and it bills.
            </p>
          </div>
          <div class="competitors-actions">
            <button
              type="button"
              class="btn btn--sm"
              (click)="runSerpRivalKeywords()"
              [disabled]="!serpRivalKeywordsCanRun()"
              [title]="'Re-fetch keywords for rival pages that still have none'">
              {{ serpRivalKeywordsRunning || serpRivalKeywordsBuilding() ? 'Fetching…' : 'Re-fetch missing keywords' }}
            </button>
          </div>
        </div>

        <p class="muted" *ngIf="serpRivalKeywordsLoading">Loading keyword preview…</p>
        <p class="step-note step-note-warn" *ngIf="serpRivalKeywordsError && !serpRivalKeywordsLoading">{{ serpRivalKeywordsError }}</p>

        <ng-container *ngIf="serpRivalKeywords && !serpRivalKeywordsLoading">
          <div class="top-serp-rivals-summary">
            <span><strong>{{ serpRivalKeywords.uniqueRivalCount | number }}</strong> unique rival page(s)</span>
            <span>·</span>
            <span><strong>{{ serpRivalKeywords.needsFetchCount | number }}</strong> need Labs fetch</span>
            <span class="muted" *ngIf="serpRivalKeywords.alreadyHaveKeywordsCount > 0">
              ({{ serpRivalKeywords.alreadyHaveKeywordsCount | number }} already have keywords)
            </span>
            <span>·</span>
            <span>est. <strong>${{ serpRivalKeywords.estimatedCost }}</strong></span>
            <span class="muted" *ngIf="serpRivalKeywords.noAnalysisCount > 0">
              · {{ serpRivalKeywords.noAnalysisCount | number }} skipped (no crawl)
            </span>
          </div>
          <p class="step-note muted" *ngIf="serpRivalKeywords.noAnalysisCount > 0">
            {{ serpRivalKeywords.noAnalysisCount | number }} rival page(s) couldn't be crawled in step 1 — they'll be skipped; fetch runs for the rest.
          </p>
          <p class="step-note" *ngIf="serpRivalKeywordsQueuedBehindCrawl()">
            Queued — starts by itself when the rival crawl above finishes.
          </p>
          <p class="step-note muted" *ngIf="topSerpRivalsBuilding() && !serpRivalKeywordsQueuedBehindCrawl()">
            The rival crawl is still running; a manual re-fetch has to wait for it.
          </p>

          <div class="top-serp-rivals-progress" *ngIf="serpRivalKeywordsStatus && serpRivalKeywordsStatus.status !== 'not_started'">
            <div class="tsr-progress-bar" *ngIf="serpRivalKeywordsBuilding()">
              <div class="tsr-progress-fill" [style.width.%]="serpRivalKeywordsProgressPct()"></div>
            </div>
            <p class="step-detail" *ngIf="serpRivalKeywordsBuilding()">
              {{ serpRivalKeywordsStatusLine() }}
              <span class="muted">({{ serpRivalKeywordsProgressPct() }}%)</span>
            </p>
            <p class="step-note" *ngIf="serpRivalKeywordsStatus.status === 'ready'">
              Ready — keywords stored for {{ serpRivalKeywordsStatus.fetchedUrls | number }} rival page(s).
              <span class="muted" *ngIf="serpRivalKeywordsStatus.skippedUrls > 0">
                ({{ serpRivalKeywordsStatus.skippedUrls | number }} already had keywords)
              </span>
              <span class="muted" *ngIf="(serpRivalKeywords?.noAnalysisCount ?? 0) > 0">
                ({{ serpRivalKeywords.noAnalysisCount | number }} skipped — no crawl from step 1)
              </span>
              <span class="step-note-warn" *ngIf="serpRivalKeywordsFailureCount() > 0">
                Couldn't fetch {{ serpRivalKeywordsFailureCount() | number }} page(s).
              </span>
            </p>
            <!-- This leg's own actuals; the card header sums both when chained. -->
            <p class="step-note tsr-actuals" *ngIf="serpRivalKeywordsStatus.status === 'ready' && serpRivalKeywordsStatus.fetchedUrls > 0">
              Keyword fetch took <strong>{{ formatChainDuration(serpRivalKeywordsStatus.durationMs) || 'under a second' }}</strong>
              · billed <strong>{{ formatChainUsd(serpRivalKeywordsStatus.actualCostUsd) }}</strong>
            </p>
            <p class="step-note step-note-warn" *ngIf="serpRivalKeywordsStatus.status === 'failed'">
              Fetch failed: {{ serpRivalKeywordsStatus.lastError || 'unknown error' }}
            </p>

            <div class="top-serp-rivals-failures" *ngIf="serpRivalKeywordsFailureCount() > 0">
              <button type="button" class="btn btn--link btn--sm" (click)="toggleSerpRivalKeywordsFailures()">
                {{ serpRivalKeywordsFailuresExpanded ? 'Hide' : 'Show' }} fetch failures ({{ serpRivalKeywordsFailureCount() | number }})
              </button>
              <div class="tsr-failures-list" *ngIf="serpRivalKeywordsFailuresExpanded">
                <div class="tsr-failure-row" *ngFor="let f of serpRivalKeywordsStatus.failures">
                  <span class="tsr-path" [title]="f.url">{{ formatRivalUrl(f.url) }}</span>
                  <p class="tsr-query">{{ f.error }}</p>
                </div>
              </div>
            </div>
          </div>
        </ng-container>
      </div>
    </section>
    </ng-container>
  </ng-container>
</div>
